import type {
  IStoryToon,
  IStoryToonEpisode,
  StoryToonImage,
  StoryToonImageSlot,
} from '@api/webtoon';
import { WEBTOON_STORIES } from '@constants';
import { WebtoonStoryMeta } from '@constants';

export const getWebtoonStoryMeta = (storyId?: number | null) => {
  if (!storyId) {
    return undefined;
  }

  return WEBTOON_STORIES.find((story) => story.id === storyId);
};

export const parseWebtoonRouteNumber = (
  value: string | string[] | undefined
) => {
  const rawValue = Array.isArray(value) ? value[0] : value;
  const parsedValue = Number(rawValue);

  return Number.isSafeInteger(parsedValue) && parsedValue > 0
    ? parsedValue
    : undefined;
};

export const getStoryToonId = (storyToon?: IStoryToon) =>
  storyToon?.webtoonId ?? storyToon?.storyToonId ?? storyToon?.id;

export const getWebtoonStoryPath = (storyId: number) => `/webtoon/${storyId}`;

export const getWebtoonEpisodePath = (storyId: number, episodeNumber: number) =>
  `/webtoon/${storyId}/${episodeNumber}`;

export const getWebtoonEpisodeByNumber = <T>(
  episodes: T[],
  episodeNumber?: number
) => {
  if (!episodeNumber) {
    return undefined;
  }

  return episodes[episodeNumber - 1];
};

export const getStoryToonTitle = (
  storyToon?: IStoryToon,
  webtoonMeta?: WebtoonStoryMeta
) => storyToon?.title ?? storyToon?.story?.title ?? webtoonMeta?.title ?? '';

export const getStoryToonSubtitle = (
  storyToon?: IStoryToon,
  webtoonMeta?: WebtoonStoryMeta
) =>
  storyToon?.subtitle ??
  storyToon?.summary ??
  storyToon?.story?.summary ??
  webtoonMeta?.subtitle ??
  '';

export const getStoryToonAuthorName = (storyToon?: IStoryToon) =>
  storyToon?.story?.user?.nickname ?? '';

export const getStoryToonCoverImage = (storyToon?: IStoryToon) =>
  storyToon?.thumbnailImage ??
  storyToon?.coverImage ??
  storyToon?.mainImage ??
  storyToon?.image ??
  storyToon?.story?.coverImage;

export const getStoryToonEpisodeNumber = (
  episode: IStoryToonEpisode,
  index: number
) => episode.episodeNumber ?? episode.number ?? episode.order ?? index + 1;

export const getStoryToonEpisodeByNumber = (
  episodes: IStoryToonEpisode[],
  episodeNumber?: number
) => {
  if (!episodeNumber) {
    return undefined;
  }

  return (
    episodes.find(
      (episode, index) =>
        getStoryToonEpisodeNumber(episode, index) === episodeNumber
    ) ?? episodes[episodeNumber - 1]
  );
};

const getImageFromSlot = (
  slot?: StoryToonImageSlot
): StoryToonImage | undefined => {
  if (!slot) {
    return undefined;
  }

  if ('attachment' in slot) {
    return slot;
  }

  return slot.image ?? slot.file;
};

const isStoryToonImage = (image?: StoryToonImage): image is StoryToonImage =>
  !!image?.attachment?.url;

export const getStoryToonEpisodeImages = (episode?: IStoryToonEpisode) => {
  if (!episode) {
    return [];
  }

  return [
    ...(episode.content?.images ?? []),
    ...(episode.images ?? []),
    ...(episode.episodeImages ?? []),
    ...(episode.toonImages ?? []),
    ...(episode.imageFiles ?? []),
    ...(episode.contentImages ?? []),
    ...(episode.files ?? []),
    ...((episode.cuts ?? []).map(getImageFromSlot) ?? []),
    ...((episode.pages ?? []).map(getImageFromSlot) ?? []),
    episode.contentImage,
    episode.imageFile,
    episode.image,
  ].filter(isStoryToonImage);
};

export const getStoryToonEpisodeThumbnail = (episode?: IStoryToonEpisode) =>
  episode?.thumbnailImage ?? getStoryToonEpisodeImages(episode)[0];
